/**
 * @file Google reCAPTCHA helpers.
 */

import { wvusLogger } from "../observability/logging";

/**
 * Waits for the reCAPTCHA API to be ready within a specified timeout period.
 *
 * @param timeout - The maximum time to wait for the reCAPTCHA API to be ready, in milliseconds. Defaults to 7000 ms (7 seconds).
 * @returns A promise that resolves when the reCAPTCHA API is ready, or rejects if the timeout is reached or an error occurs.
 *
 * @throws {Error} If the reCAPTCHA ready function times out or an error occurs while waiting.
 */
export const reCaptchaReady = (timeout: number = 7000): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      reject(new Error(`reCAPTCHA ready function timed out after ${timeout / 1000} seconds`));
    }, timeout);

    try {
      window.grecaptcha.ready(() => {
        if (!timedOut) {
          clearTimeout(timeoutId);
          resolve();
        }
      });
    } catch (error) {
      clearTimeout(timeoutId);
      reject(error);
    }
  });
};

/**
 * Retrieves the reCAPTCHA token for a given order. Times out after a specified period and logs an error.
 *
 * @param orderId - The unique identifier for the order.
 * @param timeout - The maximum time to wait for the reCAPTCHA library to be ready, in milliseconds. Defaults to 7000 ms.
 * @returns A promise that resolves to the reCAPTCHA token as a string.
 * @throws Will log an error and return an empty string if the reCAPTCHA execution fails.
 */
export const getRecaptchaToken = async (
  orderId: string,
  timeout: number = 7000
): Promise<string> => {
  try {
    const google_recaptcha_site_key =
      window.wvusFlexCheckoutSettings?.google_recaptcha_site_key || "";
    await reCaptchaReady(timeout);
    const token = await window.grecaptcha.execute(google_recaptcha_site_key, { action: "submit" });
    return token;
  } catch (error) {
    wvusLogger.log(
      {
        orderId: orderId,
        errorMessage: error.message || "Unknown error",
        parentUrl: window?.location?.href
      },
      wvusLogger.getLogLevelNames().CRITICAL
    );
    console.error(error);
  }
  return "";
};
